import { ArrowChevronUpIcon } from '@tableau/icons/ArrowChevronUpIcon';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { IconStyle } from '../../icons/Icons';
import { AnimationDuration } from '../../utils/Animation';
import { makeTestId } from '../../utils/Declarative';
import { generateId } from '../../utils/DomUtil';
import { HeightAnimator } from '../../utils/HeightAnimator';
import { ElemAttrs } from '../../utils/HtmlAttrs';
import { EventUtilsOptOut, useEffectExceptOnMount } from '../../utils/ReactHelpers';
import { ExpandoChevronSize, ExpandoColorStyleNames, ExpandoStyleNames, ExpandoType } from './ExpandoStyles';

export type ExpandoAlignment = 'left' | 'right';
export { ExpandoType };

export type ExpandoWidgetProps = {
  /**
   * Determines if the expando caret is aligned to the left or right
   * @default 'left'
   */
  alignment?: ExpandoAlignment;
  children?: React.ReactNode;
  /** NOTE: Applied to the root element, not the focusable button element. */
  className?: string;
  /**
   * Whether the expando expands up or down
   * Ultimately, this changes whether the control is above or below the expandable content
   * @default 'ExpandoDirection.Down'
   */
  direction?: ExpandoDirection;
  /**
   * The type of expando style to display
   * @default 'default'
   */
  expandoType?: ExpandoType;
  /** Label for the expando button */
  label?: React.ReactNode;
  /** The icon to be displayed to the left of `label` when rendered */
  labelIcon?: React.ReactElement | null;
  /**
   * By default children will be removed when collapsed
   * Setting this prop to `true` will keep them in the DOM with `display: none` instead
   */
  keepChildrenMounted?: boolean;
  /** No background color when `isExpanded` is set to true */
  noBackgroundWhenExpanded?: boolean;
  /** NOTE: Applied to the root element, not the focusable button element. */
  style?: React.CSSProperties;
  /** The sub-label to be displayed below `label` when rendered */
  subLabel?: React.ReactNode;
  /**
   * Test id to be attached to the dialog and its contents.
   * The suffix '-Expando' will be automatically added
   */
  testId?: string;
} & ({ // controlled mode (i.e. "state" managed by the parent component)
  /** When using the Expando in its controlled mode, set to `true` when expanded. The callbacks `onCollapse` and `onExpand` are required in this mode. */
  isExpanded: boolean;
  isInitiallyExpanded?: never;
  /** Invoked when the user wishes to collapse the Expando. Required when the Expando is used in controlled mode, i.e. when specifying the `isExpanded` prop. */
  onCollapse: () => void;
  /** Invoked when the user wishes to expand the Expando. Required when the Expando is used in controlled mode, i.e. when specifying the `isExpanded` prop. */
  onExpand: () => void;
} | { // uncontrolled mode (i.e. state managed by the Expando itself)
  isExpanded?: never;
  /** Used to default to expanded state when using the Expando in its uncontrolled mode. */
  isInitiallyExpanded?: boolean;
  /** Invoked when the Expando collapses. */
  onCollapse?: () => void;
  /** Invoked when the Expando expands. */
  onExpand?: () => void;
});

export enum ExpandoDirection {
  Up = 0,
  Down = 1,
}

export type ExpandoInstance = {
  /**
   * Toggles the Expando.
   * @param expanded optional parameter, ```true``` to open or ```false``` to close
   */
  toggle: (expanded?: boolean) => void;
};

// In the collapsed state, caret (which originally points up) either points right or left
const CollapsedIconTransform: { [key in ExpandoAlignment]: string } = Object.freeze({
  left: 'rotate(90deg)',
  right: 'rotate(-90deg)',
});

// In the expanded state, caret (which originally points up) either points up or down
const ExpandedIconTransform: { [key: string]: string } = Object.freeze({
  [`${ExpandoDirection.Up}left`]: 'rotate(0deg)',
  [`${ExpandoDirection.Up}right`]: 'rotate(0deg)',
  [`${ExpandoDirection.Down}left`]: 'rotate(180deg)',
  [`${ExpandoDirection.Down}right`]: 'rotate(-180deg)',
});

const ExpandoBaseTestId = 'Expando';

/**
 * Expands/collapses to show/hide content. Can be used as either a controlled or uncontrolled component.
 *
 * In controlled mode, the parent is responsible for controlling expansion state via the `isExpanded` prop, signaled by the `onCollapse` and `onExpand` props.
 *
 * In uncontrolled mode, the state is handled internally by the Expando, and can be defaulted using the `isInitiallyExpanded` prop.
 *
 * **NOTE**: all HTML props are applied to the focusable button element, with the exception of `style` and `className`, which are applied
 * to the root wrapper element.
 */
export const ExpandoWidget = React.forwardRef<ExpandoInstance, ExpandoWidgetProps & Omit<
  ElemAttrs, // intentionally using `ElemAttrs` (no `disabled` prop), and we could change to `ButtonAttrs` if there's ever a real use case for disabled expando
  'onClick' | keyof ExpandoWidgetProps
// eslint-disable-next-line max-len
>>(({ alignment, children, className, direction, expandoType, isExpanded: isControlledExpanded, isInitiallyExpanded, label, labelIcon, keepChildrenMounted, noBackgroundWhenExpanded, onCollapse, onExpand, style, subLabel, testId, ...htmlAttrs }, ref) => {
  const isUncontrolledMode = isControlledExpanded === undefined;
  const [isUncontrolledExpanded, setExpanded] = React.useState(isInitiallyExpanded);
  useEffectExceptOnMount(() => (isUncontrolledExpanded ? onExpand : onCollapse)?.(), [isUncontrolledExpanded]);

  const isExpanded = isUncontrolledMode ? isUncontrolledExpanded : isControlledExpanded;

  const toggle: ExpandoInstance['toggle'] = (expanded = !isExpanded) => {
    if (isUncontrolledMode) {
      setExpanded(expanded);
    } else {
      (expanded ? onExpand : onCollapse)?.();
    }
  };
  React.useImperativeHandle(ref, (): ExpandoInstance => ({ toggle }));

  const [contentId] = React.useState(generateId());
  const contentHeight = isExpanded ? 'auto' : 0;

  const expandoIconStyle: React.CSSProperties = {
    ...IconStyle,
    height: 'var(--expando-chevron-size)',
    width: 'var(--expando-chevron-size)',
    transform: isExpanded ?
      ExpandedIconTransform[`${direction!}${alignment!}`] :
      // collapsed state only cares about alignment, not direction
      CollapsedIconTransform[alignment!],
    transition: `transform ${AnimationDuration}ms`,
    ...(label || labelIcon) && {
      // add some margin when there is another element right next to the expando icon
      marginLeft: alignment === 'right' ? 8 : 0,
      marginRight: alignment === 'left' ? 8 : 0,
    },
    ...(alignment === 'right') && { order: 1 },
  };

  const cssFlags: Parameters<typeof TypeStyle.classes>[0] = {
    [`${alignment}-align`]: true,
    [`${direction === ExpandoDirection.Up ? 'up' : 'down'}-direction`]: true,
    hasLeftBorder: expandoType !== 'default' && expandoType !== 'neutral',
    noBackgroundWhenExpanded,
  };

  return (
    <div
      className={TypeStyle.classes(
        className,
        ExpandoColorStyleNames[expandoType!],
        ExpandoStyleNames.rootStyleName,
        {
          [ExpandoStyleNames.legacyAlignmentStyleName]: expandoType === 'default' && alignment === 'left',
        }
      )}
      data-tb-test-id={makeTestId(testId, ExpandoBaseTestId)}
      style={style}
    >
      <button
        {...EventUtilsOptOut}
        data-tb-test-id={makeTestId(testId, ExpandoBaseTestId, 'Button')}
        {...htmlAttrs}
        {...(isExpanded && { 'aria-controls': contentId })}
        aria-expanded={isExpanded}
        className={TypeStyle.classes(ExpandoStyleNames.buttonStyleName, cssFlags)}
        onClick={() => toggle()}
      >
        <span key='label'>
          <ArrowChevronUpIcon key='expandoIcon'
            aria-hidden
            color='var(--expando-chevron-color)'
            data-tb-test-id={makeTestId(testId, ExpandoBaseTestId, 'Icon')}
            size={ExpandoChevronSize}
            style={expandoIconStyle}
            viewBox={`0 0 ${ExpandoChevronSize} ${ExpandoChevronSize}`}
          />
          {labelIcon &&
            <span className={ExpandoStyleNames.labelIconStyleName} data-tb-test-id={makeTestId(testId, ExpandoBaseTestId, 'LabelIcon')}>
              {labelIcon}
            </span>
          }
          {label &&
            <span key='expandoLabel' className={ExpandoStyleNames.labelStyleName} data-tb-test-id={makeTestId(testId, ExpandoBaseTestId, 'Label')}>
              {label}
            </span>
          }
        </span>
        {subLabel &&
          <span key='sublabel'
            className={ExpandoStyleNames.subLabelStyleName}
            data-tb-test-id={makeTestId(testId, ExpandoBaseTestId, 'SubLabel')}
          >
            {subLabel}
          </span>
        }
      </button>
      <HeightAnimator key='content'
        {...(keepChildrenMounted && { height: contentHeight })}
        initialHeight={contentHeight}
      >
        {(keepChildrenMounted || isExpanded) && <div
          className={TypeStyle.classes(ExpandoStyleNames.contentStyleName, cssFlags)}
          data-tb-test-id={makeTestId(testId, ExpandoBaseTestId, 'Content')}
          id={contentId}
        >
          {children}
        </div>}
      </HeightAnimator>
    </div>
  );
});
ExpandoWidget.defaultProps = { alignment: 'left', direction: ExpandoDirection.Down, expandoType: 'default' };
ExpandoWidget.displayName = 'ExpandoWidget';
